import { compassBearing }        from '@tokamap/core';
import { lngLatToMeters }        from '@tokamap/core';
import { metersToLngLat }        from '@tokamap/core';
import { defaultFlightRise }     from '@tokamap/core';
import { freeFlightLength }      from '@tokamap/core';
import { isFreeFlight }          from '@tokamap/core';
import { DECOR_MODELS_BY_ID }    from '@tokamap/core';
import type { DecorItem }        from '@tokamap/core';
import type { DecorModel }       from '@tokamap/core';
import type { DecorFlightTopSnap } from '@tokamap/core';
import type { DecorModelBounds } from '@tokamap/core';
import type { LngLat }           from '@tokamap/core';

export type FlightEnd = { position: LngLat; ground: number };

export type FlightPlacement = {
	position: LngLat;
	rotationDeg: number;
	depth: number;
	elevation?: number;
	slopeDeg?: number;
	top: LngLat;
	bottom: LngLat;
};

const FLIGHT_LEVEL_TOLERANCE = 0.3;
const FLIGHT_MIN_RISE = 0.3;
const FLIGHT_MAX_RISE = 40;
const FLIGHT_MIN_DISTANCE = 0.1;

export const MIN_DECOR_SCALE = 0.2;
export const MAX_DECOR_SCALE = 5;

export function decorPoint(item: Pick<DecorItem, 'position' | 'rotationDeg' | 'scale'>, x: number, y: number): LngLat {
	const angle = item.rotationDeg * Math.PI / 180;
	const east = (x * Math.cos(angle) + y * Math.sin(angle)) * item.scale;
	const north = (-x * Math.sin(angle) + y * Math.cos(angle)) * item.scale;

	return metersToLngLat(item.position, east, north);
}

export function decorFootprint(item: Pick<DecorItem, 'position' | 'rotationDeg' | 'scale'>, bounds: DecorModelBounds): LngLat[] {
	return [
		decorPoint(item, bounds.minX, bounds.maxY),
		decorPoint(item, bounds.maxX, bounds.maxY),
		decorPoint(item, bounds.maxX, bounds.minY),
		decorPoint(item, bounds.minX, bounds.minY),
	];
}

export function decorFront(item: Pick<DecorItem, 'position' | 'rotationDeg' | 'scale'>, bounds: DecorModelBounds): LngLat {
	return decorPoint(item, (bounds.minX + bounds.maxX) / 2, bounds.maxY);
}

export function decorSize(item: Pick<DecorItem, 'scale'>, bounds: DecorModelBounds) {
	return {
		length: (bounds.maxY - bounds.minY) * item.scale,
		width: (bounds.maxX - bounds.minX) * item.scale,
		height: bounds.height * item.scale,
	};
}

export function clampDecorScale(scale: number): number {
	return Math.min(MAX_DECOR_SCALE, Math.max(MIN_DECOR_SCALE, scale));
}

export function translatePath(path: LngLat[] | undefined, delta: LngLat): LngLat[] | undefined {
	return path?.map(([lng, lat]) => [lng + delta[0], lat + delta[1]]);
}

export function freeFlightModelOf(modelId: string): DecorModel | null {
	const model = DECOR_MODELS_BY_ID[modelId];
	return model && isFreeFlight(model) ? model : null;
}

const roundTenth = (value: number) => Math.round(value * 10) / 10;

function riseForLength(model: DecorModel, length: number, slopeDeg?: number): number {
	let low = FLIGHT_MIN_RISE;
	let high = FLIGHT_MAX_RISE;
	if (freeFlightLength(model, low, slopeDeg) >= length) return low;
	for (let i = 0; i < 30; i++) {
		const middle = (low + high) / 2;
		if (freeFlightLength(model, middle, slopeDeg) <= length) low = middle;
		else high = middle;
	}
	return low;
}

/** Volée accrochée à un bord par son palier du haut, descendant jusqu'au niveau qui le borde. */
export function flightFromTop(model: DecorModel, snap: DecorFlightTopSnap, low = snap.low, slopeDeg?: number): FlightPlacement {
	const depth = roundTenth(snap.height - low);
	const angle = snap.rotationDeg * Math.PI / 180;
	const half = freeFlightLength(model, depth, slopeDeg) / 2;
	const position = metersToLngLat(snap.position, Math.sin(angle) * half, Math.cos(angle) * half);

	return {
		position,
		rotationDeg: snap.rotationDeg,
		depth,
		elevation: low,
		slopeDeg,
		top: snap.position,
		bottom: metersToLngLat(position, Math.sin(angle) * half, Math.cos(angle) * half),
	};
}

/**
 * Volée posée entre deux points visés, à la pente `slopeDeg` (celle des marches standard sans elle).
 * D'un niveau à l'autre, elle comble leur écart, son palier du haut calé sur le bord `topSnap` quand
 * il y en a un : la pente règle sa longueur. Sur un même niveau, elle part du premier point — son
 * pied — jusqu'au second : la pente règle sa hauteur.
 */
export function flightBetween(model: DecorModel, start: FlightEnd, end: FlightEnd, topSnap: DecorFlightTopSnap | null, slopeDeg?: number): FlightPlacement | null {
	const reach = lngLatToMeters(start.position, end.position);
	const distance = Math.hypot(reach.east, reach.north);
	if (distance < FLIGHT_MIN_DISTANCE) return null;

	const [low, high] = start.ground <= end.ground ? [start, end] : [end, start];
	if (topSnap && topSnap.height - low.ground >= FLIGHT_LEVEL_TOLERANCE) return flightFromTop(model, topSnap, low.ground, slopeDeg);

	const climbs = high.ground - low.ground >= FLIGHT_LEVEL_TOLERANCE;
	const toward = climbs ? lngLatToMeters(low.position, high.position) : reach;
	const span = Math.hypot(toward.east, toward.north);
	if (span < FLIGHT_MIN_DISTANCE) return null;

	const depth = roundTenth(climbs ? high.ground - low.ground : riseForLength(model, distance, slopeDeg));
	const half = freeFlightLength(model, depth, slopeDeg) / 2;
	const east = toward.east / span * half;
	const north = toward.north / span * half;
	const position = climbs ? metersToLngLat(high.position, -east, -north) : metersToLngLat(start.position, east, north);
	const far = metersToLngLat(position, east, north);
	const near = metersToLngLat(position, -east, -north);

	return {
		position,
		rotationDeg: compassBearing(far, near),
		depth,
		elevation: climbs ? low.ground : undefined,
		slopeDeg,
		top: far,
		bottom: near,
	};
}

/** La même volée à une autre pente, son palier du haut restant en place : c'est le pied qui se déplace. */
export function reslopedFlight(model: DecorModel, item: Pick<DecorItem, 'position' | 'rotationDeg' | 'scale' | 'depth' | 'slopeDeg'>, slopeDeg: number): Pick<DecorItem, 'position' | 'slopeDeg'> {
	const rise = item.depth ?? defaultFlightRise(model);
	const before = freeFlightLength(model, rise, item.slopeDeg) * item.scale / 2;
	const after = freeFlightLength(model, rise, slopeDeg) * item.scale / 2;
	const angle = item.rotationDeg * Math.PI / 180;
	const top = metersToLngLat(item.position, -Math.sin(angle) * before, -Math.cos(angle) * before);

	return { position: metersToLngLat(top, Math.sin(angle) * after, Math.cos(angle) * after), slopeDeg };
}
